import { PmiButton } from 'shell/components/shared/button';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { memo, ReactElement, useMemo } from 'react';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { PracticeFamily } from 'shell/models/practice-family';
import AccordionFamily from './family';
import AccordionPerson from './person';
import { PersonFormVM } from './person/form';
import { SubjectData } from 'shell/models/person';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { noop } from 'shell/utils/noop';
import { FamilyVM } from './family/family-vm';
import { useAireSubscription } from 'subscription/context/aire-subscription';

interface StepFamilyProps {
  onAvanza?: () => void;
  onIndietro?: () => void;
}

const StepFamilyComponent = ({ onAvanza, onIndietro }: StepFamilyProps): ReactElement => {
  const { praticaKey, intestatarioData } = useAireSubscription();

  const [familyMembers] = useHttpFetch<HttpResponseList<PracticeFamily>, Array<FamilyVM>>(
    `${__PRACTICE_API_URL__}/visualizzazione-scelta-famiglia`,
    {
      onBootstrap: !!praticaKey,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: praticaKey
          ? {
              key: {
                codiceEnte: praticaKey.cdEnte,
                tipoRegistro: 'IAIRE',
                idAnnoPratica: praticaKey.idAnnoPratica,
                progressivoPratica: praticaKey.pgPratica,
              },
            }
          : {},
      },
      transformer: (response) => {
        return response.data.payload?.list
          ?.filter((item) => item?.posizionePersona !== 'X')
          ?.map((subject) => ({
            surname: subject.generalita?.cognome || '',
            name: subject.generalita?.nome || '',
            fiscalCode: subject.generalita?.codiceFiscale?.codFiscale || '',
            birthDate: subject.generalita?.dataNascita
              ? DateTime.fromISO(subject.generalita?.dataNascita).toFormat('dd/MM/yyyy')
              : '',
            relationship: subject.legameSoggetto?.codiceLegame || '',
            oldRelationship:
              subject.vecchioCodiceLegame || subject.legameSoggetto?.codiceLegame || '',
            birthplace:
              (subject.generalita?.luogoNascita?.localita
                ? subject.generalita?.luogoNascita?.localita?.descrizioneStato
                : subject.generalita?.luogoNascita?.comune?.nomeComune) || '',
            residence: subject.indirizzo || '',
            drivingLicense: subject.autoveicoli?.possessoPatente === 'S' ? 'S' : 'N',
            auto: subject.autoveicoli?.possessoAutoveicoli === 'S' ? 'S' : 'N',
            aire: subject.generalita?.soggettoAIRE || 'N',
            cfControl: subject.controlloCf || '1',
            subject,
            selectedFromDb: (subject.selected && subject.intestatario) || false,
            checked: subject.selected || false,
            idAnprIntestatario: subject.idAnprIntestatario || '',
            intestatario: subject.intestatario || false,
            richiedente: subject.flagRichiedente || false,
            isPersonaNonResidente: subject.personaNonResidente === '1',
            isMaiAnagrafato: subject.personaNonResidente === '2',
            tipoCasoPratica: subject.tipoCasoPratica?.toString() || '',
            tipoLegame: subject.legameSoggetto?.tipoLegame || '',
            idSchedaSoggettoANPR: subject.generalita?.idSchedaSoggettoANPR || '',
            idIntestatario: subject.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
            isEditGenerality:
              subject.personaNonResidente === '1' || subject.personaNonResidente === '2', // se irriperibili o mai anagrafato
            generality: subject.generalita
              ? transformGenerality(subject as SubjectData)
              : undefined,
          }));
      },
    }
  );

  const data = useMemo(() => {
    const apiData = (familyMembers.data as Array<FamilyVM>) || [];

    if (apiData.length === 0 && intestatarioData) {
      const subjectData = intestatarioData as SubjectData;

      const familyMemberFromIntestatario: FamilyVM = {
        surname: subjectData.generalita?.cognome || '',
        name: subjectData.generalita?.nome || '',
        fiscalCode: subjectData.generalita?.codiceFiscale?.codFiscale || '',
        birthDate: subjectData.generalita?.dataNascita
          ? DateTime.fromISO(subjectData.generalita?.dataNascita).toFormat('dd/MM/yyyy')
          : '',
        relationship: '',
        oldRelationship: '',
        birthplace:
          (subjectData.generalita?.luogoNascita?.localita
            ? subjectData.generalita?.luogoNascita?.localita?.descrizioneStato
            : subjectData.generalita?.luogoNascita?.comune?.nomeComune) || '',
        residence: '',
        drivingLicense: subjectData.autoveicoli?.possessoPatente === 'S' ? 'S' : 'N',
        auto: subjectData.autoveicoli?.possessoAutoveicoli === 'S' ? 'S' : 'N',
        aire: subjectData.generalita?.soggettoAIRE || 'S',
        cfControl: '1',
        subject: subjectData,
        selectedFromDb: false,
        checked: true,
        idAnprIntestatario: '',
        intestatario: true,
        richiedente: false,
        isPersonaNonResidente: false,
        isMaiAnagrafato: false,
        tipoCasoPratica: '',
        tipoLegame: '',
        idSchedaSoggettoANPR: subjectData.generalita?.idSchedaSoggettoANPR || '',
        idIntestatario: subjectData.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
        isEditGenerality: false,
        generality: subjectData.generalita ? transformGenerality(subjectData) : undefined,
      };

      return [familyMemberFromIntestatario];
    }

    return apiData;
  }, [familyMembers.data, intestatarioData]);

  const formContext = useFormik<{
    family: Array<FamilyVM>;
    person?: PersonFormVM;
  }>({
    initialValues: { family: data },
    onSubmit: noop,
  });

  return (
    <>
      <AccordionFamily formContext={formContext} />
      <AccordionPerson formContext={formContext} />
      <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
        <div className="pmi-w-25 pmi-w-lg-15">
          <PmiButton
            variant="outlined"
            fullWidth
            color="cdmBrand"
            onClick={() => {
              if (onIndietro) {
                onIndietro();
              }
            }}
          >
            Indietro
          </PmiButton>
        </div>
        <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
          <PmiButton
            variant="outlined"
            fullWidth
            color="cdmBrandThird"
            onClick={() => {
              if (onAvanza) {
                onAvanza();
              }
            }}
          >
            Avanti
          </PmiButton>
        </div>
      </div>
    </>
  );
};

StepFamilyComponent.displayName = 'step family component';

const StepFamily = memo(StepFamilyComponent);

export default StepFamily;
